import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Title, Text, Stack, Group, Button,
  Paper, SimpleGrid, Badge, Alert, Loader, Table, Code, Progress,
} from '@mantine/core';
import {
  IconAlertCircle, IconRefresh,
  IconCircleCheck, IconCircleX, IconCircleMinus, IconTerminal2,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { useAuth } from '../../shared/contexts/AuthContext';
import { ivrAdminStatus, ivrIngestionLog, ivrIngestionProgress } from './api_scripts/ivrClient';

const POLL_INTERVAL_MS = 6000;

function StatCard({ label, value, color }) {
  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>{label}</Text>
      <Text size="xl" fw={700} c={color || 'dark'}>{value ?? '—'}</Text>
    </Paper>
  );
}

function StatusIcon({ status }) {
  if (status === 'success') return <IconCircleCheck size={16} style={{ color: '#2f9e44' }} />;
  if (status === 'error') return <IconCircleX size={16} style={{ color: '#e03131' }} />;
  return <IconCircleMinus size={16} style={{ color: '#868e96' }} />;
}

function formatElapsed(secs) {
  if (!secs) return '0s';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function IngestionProgressPanel({ progress }) {
  if (!progress || progress.status === 'idle') return null;

  const isRunning = progress.status === 'running';
  const pct = progress.files_total > 0
    ? Math.round((progress.files_done / progress.files_total) * 100)
    : 0;

  const statusColor = isRunning ? 'blue' : 'green';
  const statusLabel = isRunning ? 'Running' : 'Complete';

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder style={{ borderColor: isRunning ? '#a5d8ff' : '#b2f2bb' }}>
      <Group justify="space-between" align="center" mb="sm">
        <Group gap={8}>
          {isRunning && <Loader size="xs" color="blue" />}
          <Text fw={600} size="sm">Ingestion {statusLabel}</Text>
          <Badge color={statusColor} variant="light" size="sm">{statusLabel}</Badge>
        </Group>
        <Text size="xs" c="dimmed">
          Elapsed: {formatElapsed(progress.elapsed_seconds)}
          {progress.started_at && (
            <> · Started {new Date(progress.started_at).toLocaleTimeString()}</>
          )}
        </Text>
      </Group>

      <Progress
        value={pct}
        color={isRunning ? 'blue' : 'green'}
        size="md"
        radius="sm"
        mb="sm"
        striped={isRunning}
        animated={isRunning}
      />

      <Group gap={4} mb="sm">
        <Text size="xs" c="dimmed" fw={500}>
          {progress.files_done} / {progress.files_total} files
        </Text>
        <Text size="xs" c="dimmed">·</Text>
        <Text size="xs" c="green" fw={500}>✓ {progress.success} ok</Text>
        <Text size="xs" c="dimmed">·</Text>
        <Text size="xs" c="dimmed" fw={500}>↷ {progress.skipped} skipped</Text>
        <Text size="xs" c="dimmed">·</Text>
        <Text size="xs" c={progress.errors > 0 ? 'red' : 'dimmed'} fw={500}>
          ✗ {progress.errors} errors
        </Text>
        {progress.use_cases_found > 0 && (
          <>
            <Text size="xs" c="dimmed">·</Text>
            <Text size="xs" c="indigo" fw={500}>◆ {progress.use_cases_found} use cases found</Text>
          </>
        )}
      </Group>

      {progress.recent_files && progress.recent_files.length > 0 && (
        <Box style={{ maxHeight: 200, overflowY: 'auto', borderRadius: 6, border: '1px solid #e9ecef' }}>
          <Table striped style={{ fontSize: 12 }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 28 }}></Table.Th>
                <Table.Th>File</Table.Th>
                <Table.Th style={{ width: 72 }}>Status</Table.Th>
                <Table.Th style={{ width: 72 }}>Use Cases</Table.Th>
                <Table.Th style={{ width: 60 }}>Time</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {progress.recent_files.map((f, i) => (
                <Table.Tr key={i}>
                  <Table.Td><StatusIcon status={f.status} /></Table.Td>
                  <Table.Td>
                    <Text size="xs" style={{ wordBreak: 'break-all' }} lineClamp={1}>{f.name}</Text>
                    {f.reason && <Text size="10px" c="dimmed">{f.reason}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={f.status === 'success' ? 'green' : f.status === 'error' ? 'red' : 'gray'} variant="light">
                      {f.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="xs">{f.use_cases ?? '—'}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{f.secs != null ? `${f.secs}s` : '—'}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}

export default function IvrAdmin() {
  const { getApiToken } = useAuth();
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingLog, setLoadingLog] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    setStatusError(null);
    try {
      setStatus(await ivrAdminStatus(getApiToken));
    } catch (err) {
      setStatusError(err.message);
    } finally {
      setLoadingStatus(false);
    }
  }, [getApiToken]);

  const loadLog = useCallback(async () => {
    setLoadingLog(true);
    try {
      setLog(await ivrIngestionLog(getApiToken));
    } catch (e) { /* non-critical */ }
    finally { setLoadingLog(false); }
  }, [getApiToken]);

  const loadProgress = useCallback(async () => {
    try {
      const data = await ivrIngestionProgress(getApiToken);
      setProgress(data);
      return data;
    } catch (e) { /* non-critical */ }
  }, [getApiToken]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const data = await loadProgress();
      if (data && data.status !== 'running') {
        stopPolling();
        loadStatus();
        loadLog();
      }
    }, POLL_INTERVAL_MS);
  }, [loadProgress, loadStatus, loadLog, stopPolling]);

  useEffect(() => {
    loadStatus();
    loadLog();
    loadProgress().then((data) => {
      if (data && data.status === 'running') startPolling();
    });
    return () => stopPolling();
  }, [loadStatus, loadLog, loadProgress, startPolling, stopPolling]);

  const handleRefresh = () => {
    loadStatus();
    loadLog();
    loadProgress().then((data) => {
      if (data && data.status === 'running') startPolling();
      else stopPolling();
    });
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column' }}>

      <Box style={{ padding: '32px 40px 24px', borderBottom: '1px solid #e9ecef', background: '#ffffff' }}>
        <Group justify="space-between" align="center">
          <Box>
            <Title order={2} size="1.5rem" fw={700}>Admin</Title>
            <Text c="dimmed" size="sm" mt={4}>Ingestion status and controls</Text>
          </Box>
          <Button
            leftSection={<IconRefresh size={14} />}
            variant="subtle"
            color="indigo"
            size="sm"
            loading={loadingStatus}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Group>
      </Box>

      <Box style={{ padding: '24px 40px 32px' }}>
        <Stack gap="lg">

          {statusError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title="Status error">
              {statusError}
            </Alert>
          )}

          {loadingStatus && !status && (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <Loader color="indigo" />
            </Box>
          )}

          {status && (
            <>
              <Group gap="sm">
                <Badge
                  color={status.vectordb_ready ? 'green' : 'red'}
                  variant="filled"
                  size="md"
                >
                  {status.vectordb_ready ? '✓ OpenSearch Ready' : '✗ OpenSearch Unavailable'}
                </Badge>
                {status.last_run && (
                  <Text size="xs" c="dimmed">
                    Last run: {new Date(status.last_run).toLocaleString()}
                  </Text>
                )}
              </Group>
              <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} gap="sm">
                <StatCard label="Use Cases" value={status.total_use_cases} color="indigo" />
                <StatCard label="Remaining" value={status.total_in_inventory} />
                <StatCard label="Indexed Files" value={status.total_processed} color="green" />
                <StatCard label="Skipped (last run)" value={status.skipped} />
                <StatCard label="Errors (last run)" value={status.errored} color={status.errored > 0 ? 'red' : undefined} />
              </SimpleGrid>
            </>
          )}

          <IngestionProgressPanel progress={progress} />

          <Paper shadow="sm" p="lg" radius="md" withBorder>
            <Group gap={8} mb="md">
              <IconTerminal2 size={16} color="#4263eb" />
              <Text fw={600} size="sm">Triggering Ingestion</Text>
            </Group>
            <Text size="sm" c="dimmed" mb="md">
              Ingestion is controlled out-of-band via a CLI script. Run from your terminal with active AWS credentials:
            </Text>
            <Stack gap={8}>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Dry run — preview what would be processed</Text>
                <Code block style={{ fontSize: 12 }}>bash scripts/trigger-ivr-ingestion.sh --dry-run</Code>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Process new files since a date</Text>
                <Code block style={{ fontSize: 12 }}>bash scripts/trigger-ivr-ingestion.sh --since 2025-04-01</Code>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Re-process all files (full refresh)</Text>
                <Code block style={{ fontSize: 12 }}>bash scripts/trigger-ivr-ingestion.sh --reprocess-all</Code>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Re-process in batches of 20</Text>
                <Code block style={{ fontSize: 12 }}>bash scripts/trigger-ivr-ingestion.sh --reprocess-all --limit 20</Code>
              </Box>
            </Stack>
            <Text size="xs" c="dimmed" mt="md">
              See <Text span fw={600} style={{ fontFamily: 'monospace' }}>scripts/trigger-ivr-ingestion.sh --help</Text> for all options.
            </Text>
            <Alert
              icon={<IconPlayerPlay size={14} />}
              color="blue"
              variant="light"
              mt="md"
              style={{ fontSize: 12 }}
            >
              Once ingestion starts, return to this page — the progress panel above will appear automatically and update every 6 seconds.
            </Alert>
          </Paper>

          <Paper shadow="sm" p="lg" radius="md" withBorder>
            <Text fw={600} mb="md">Ingestion Log</Text>
            {loadingLog && <Loader size="sm" color="indigo" />}
            {!loadingLog && log.length === 0 && (
              <Text c="dimmed" size="sm">No log entries found.</Text>
            )}
            {!loadingLog && log.length > 0 && (
              <Box style={{
                overflowY: 'auto',
                maxHeight: '420px',
                borderRadius: 8,
                border: '1px solid #e9ecef',
              }}>
                <Table striped highlightOnHover style={{ fontSize: 13 }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 32 }}></Table.Th>
                      <Table.Th>File</Table.Th>
                      <Table.Th style={{ width: 80 }}>Status</Table.Th>
                      <Table.Th style={{ width: 80 }}>Use Cases</Table.Th>
                      <Table.Th>Error / Reason</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {log.slice(0, 100).map((entry, idx) => (
                      <Table.Tr key={idx}>
                        <Table.Td><StatusIcon status={entry.status} /></Table.Td>
                        <Table.Td>
                          <Text size="xs" style={{ wordBreak: 'break-all' }}>
                            {entry.file_name || entry.filename || '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="xs"
                            color={entry.status === 'success' ? 'green' : entry.status === 'error' ? 'red' : 'gray'}
                            variant="light"
                          >
                            {entry.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs">{entry.use_case_count ?? '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c={entry.error ? 'red' : 'dimmed'} lineClamp={1}>
                            {entry.error || entry.reason || ''}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            )}
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}
